<template>
  <div>
    <div v-if="provider" class="tb-form-group">
      <label class="tb-label">URL</label>

      <div>
        Field mapped to data source.
      </div>
    </div>

    <tracking-link-picker
      v-else-if="trackingLinkPickerEnabled"
      :model-value="trackingLinkRid"
      :type="typeFilter"
      @update:model-value="$emit('update:tracking-link-rid', $event)"
      @select="onTrackingLinkSelect"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex';
import catchify from 'catchify';
import { createTrackingLinkMetadata } from '../../services/tracking-links';
import { generateTrackingLinkMetadataRid } from '../../utils/tracking-links';

import TrackingLinkPicker from './TrackingLinkPicker.vue';

export default {
  name: 'QRCodeUrl',
  components: {
    TrackingLinkPicker,
  },
  props: {
    url: {
      required: true,
      type: String,
    },
    shortUrl: {
      required: true,
      type: String,
    },
    trackingLinkRid: {
      type: String,
      default: '',
    },
    trackingLinkMetadataRid: {
      type: String,
      default: '',
    },
    item: {
      type: Object,
      required: true,
    },
    element: {
      type: Object,
      required: true,
    },
    provider: {
      type: Boolean,
      default: false,
    },
    entityId: {
      type: [String, Number],
      required: true,
    },
    typeFilter: {
      type: String,
      default: '',
    },
  },
  emits: [
    'update:url',
    'update:tracking-link-rid',
    'update:tracking-link-metadata-rid',
  ],
  data() {
    return {
      trackingLinkPickerEnabled: process.env.TRACKING_LINKS_SWITCH !== 'OFF',
      lastMetadataTrackingLinkRid: null,
    };
  },
  created() {
    // Track the link the existing metadata rid belongs to so re-selecting the
    // same link doesn't create a duplicate, while changing it regenerates.
    this.lastMetadataTrackingLinkRid = this.trackingLinkRid || null;
  },
  computed: {
    ...mapState({
      production(state) {
        return state.production.production;
      },
    }),
  },
  methods: {
    onTrackingLinkSelect(link) {
      // Keep the legacy permalink in sync as a fallback until live rendering
      // fully switches to `trackingLinkRid`.
      if (link?.currentDestination?.destinationUrl) {
        this.$emit('update:url', link.currentDestination.destinationUrl);
      }

      this.ensureTrackingLinkMetadata(link);
    },

    async ensureTrackingLinkMetadata(link) {
      if (!link?.trackingLinkId) {
        return;
      }

      // Reuse the existing metadata when re-selecting the same link, but
      // otherwise generate a fresh rid so the QR's `r=` param points at the
      // newly selected link rather than a stale one.
      if (this.lastMetadataTrackingLinkRid === link.rid && this.trackingLinkMetadataRid) {
        return;
      }

      this.lastMetadataTrackingLinkRid = link.rid;

      const rid = generateTrackingLinkMetadataRid();
      this.$emit('update:tracking-link-metadata-rid', rid);

      const [err, metadata] = await catchify(createTrackingLinkMetadata({
        trackingLinkId: link.trackingLinkId,
        rid,
        fields: {
          productionId: this.production?.id,
          timelineId: this.item?.timeline_id,
          resourceId: this.item?._id || this.item?.template_id,
          resourceType: this.item?.layout,
          layout: this.item?.layout,
        },
      }));

      if (err) {
        // The optimistic rid was never persisted, so clear it (as long as the
        // user hasn't since picked a different link) to allow a retry.
        if (this.lastMetadataTrackingLinkRid === link.rid && this.trackingLinkMetadataRid === rid) {
          this.lastMetadataTrackingLinkRid = null;
          this.$emit('update:tracking-link-metadata-rid', '');
        }

        return;
      }

      // Ignore a response for a link the user has since replaced, so an
      // out-of-order response can't overwrite the current selection.
      if (this.lastMetadataTrackingLinkRid !== link.rid) {
        return;
      }

      // The API substitutes a new rid on collisions, so use the persisted one.
      this.$emit('update:tracking-link-metadata-rid', metadata?.rid || rid);
    },
  },
};
</script>
